import { useCallback, useMemo } from 'react';
import type { DeviceInfo, DeviceStatus } from '@ethbridge/core';
import { useBridgeContext, useBridgeSelector } from '../context/BridgeContext';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Device type filter
 */
export type DeviceType = 'fingerprint' | 'face' | 'iris' | 'card' | string;

/**
 * Device counts by type
 */
export interface DeviceCounts {
  total: number;
  fingerprint: number;
  face: number;
  iris: number;
  card: number;
  ready: number;
  busy: number;
}

/**
 * Return type for useDevices hook
 */
export interface UseDevicesReturn {
  /** All connected devices */
  devices: DeviceInfo[];

  /** Device counts by type */
  counts: DeviceCounts;

  /** Whether any devices are connected */
  hasDevices: boolean;

  /** Whether devices are loading */
  isLoading: boolean;

  /** Get devices by type */
  getByType: (type: DeviceType) => DeviceInfo[];

  /** Get devices by status */
  getByStatus: (status: DeviceStatus) => DeviceInfo[];

  /** Get device by ID */
  getById: (id: string) => DeviceInfo | undefined;

  /** Get first available device of type */
  getFirstAvailable: (type: DeviceType) => DeviceInfo | undefined;

  /** Check if device type is available */
  hasDeviceType: (type: DeviceType) => boolean;

  /** Refresh devices list */
  refresh: () => Promise<DeviceInfo[]>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for device management
 *
 * @returns Device state and utilities
 *
 * @example
 * ```tsx
 * function DeviceList() {
 *   const {
 *     devices,
 *     counts,
 *     getByType,
 *     hasDeviceType,
 *     refresh,
 *   } = useDevices();
 *
 *   useEffect(() => {
 *     refresh();
 *   }, [refresh]);
 *
 *   return (
 *     <div>
 *       <p>Total devices: {counts.total}</p>
 *       <p>Fingerprint scanners: {counts.fingerprint}</p>
 *
 *       {hasDeviceType('fingerprint') && (
 *         <button>Capture Fingerprint</button>
 *       )}
 *
 *       <ul>
 *         {devices.map((device) => (
 *           <li key={device.id}>
 *             {device.name || device.type} - {device.status}
 *           </li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDevices(): UseDevicesReturn {
  const context = useBridgeContext();
  const devices = useBridgeSelector((state) => state.devices);

  // Compute counts
  const counts = useMemo((): DeviceCounts => {
    const result: DeviceCounts = {
      total: devices.length,
      fingerprint: 0,
      face: 0,
      iris: 0,
      card: 0,
      ready: 0,
      busy: 0,
    };

    for (const device of devices) {
      const type = device.type.toLowerCase();
      if (type.includes('fingerprint') || type.includes('a900') || type.includes('mfs')) {
        result.fingerprint++;
      } else if (type.includes('face') || type.includes('fc2')) {
        result.face++;
      } else if (type.includes('iris') || type.includes('ir1') || type.includes('ir2')) {
        result.iris++;
      } else if (type.includes('card')) {
        result.card++;
      }

      if (device.status === 'ready') {
        result.ready++;
      } else if (device.status === 'busy') {
        result.busy++;
      }
    }

    return result;
  }, [devices]);

  const hasDevices = devices.length > 0;
  const isLoading = false; // Could track loading state if needed

  // Get devices by type
  const getByType = useCallback(
    (type: DeviceType): DeviceInfo[] => {
      const typeLower = type.toLowerCase();
      return devices.filter((d) => d.type.toLowerCase().includes(typeLower));
    },
    [devices]
  );

  // Get devices by status
  const getByStatus = useCallback(
    (status: DeviceStatus): DeviceInfo[] => {
      return devices.filter((d) => d.status === status);
    },
    [devices]
  );

  // Get device by ID
  const getById = useCallback(
    (id: string): DeviceInfo | undefined => {
      return devices.find((d) => d.id === id);
    },
    [devices]
  );

  // Get first available device of type
  const getFirstAvailable = useCallback(
    (type: DeviceType): DeviceInfo | undefined => {
      const typeLower = type.toLowerCase();
      return devices.find(
        (d) => d.type.toLowerCase().includes(typeLower) && d.status === 'ready'
      );
    },
    [devices]
  );

  // Check if device type is available
  const hasDeviceType = useCallback(
    (type: DeviceType): boolean => {
      return getFirstAvailable(type) !== undefined;
    },
    [getFirstAvailable]
  );

  // Refresh devices
  const refresh = useCallback(() => {
    return context.refreshDevices();
  }, [context]);

  return useMemo(
    () => ({
      devices,
      counts,
      hasDevices,
      isLoading,
      getByType,
      getByStatus,
      getById,
      getFirstAvailable,
      hasDeviceType,
      refresh,
    }),
    [devices, counts, hasDevices, isLoading, getByType, getByStatus, getById, getFirstAvailable, hasDeviceType, refresh]
  );
}
